import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Shield, Home, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/Button';
import { useAuth } from '@/contexts/AuthContext';

export default function AccessDenied() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const from = (location.state as { from?: Location })?.from;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="rounded-full bg-red-100 p-6">
            <Shield className="h-16 w-16 text-red-600" />
          </div>
        </div>
        
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Access Denied
        </h2>
        
        <div className="mt-4 text-center">
          <p className="text-base text-gray-600">
            You don't have permission to access this page.
          </p>
          
          {user && (
            <div className="mt-4 inline-block bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3">
              <p className="text-sm text-yellow-800">
                <strong>Current Role:</strong> {user.role.replace('_', ' ').toUpperCase()}
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                This page requires different permissions.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border border-gray-200">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">What you can do:</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  <span>Contact your organization administrator to request access</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  <span>Return to your dashboard to access available features</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  <span>Check if you're signed in with the correct account</span>
                </li>
              </ul>
            </div>

            {from && (
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Attempted to access:</p>
                <code className="block text-xs bg-gray-100 rounded px-2 py-1 text-gray-700 break-all">
                  {from.pathname}
                </code>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            className="flex-1 flex items-center justify-center"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
          
          <Link to="/dashboard" className="flex-1">
            <Button className="w-full flex items-center justify-center">
              <Home className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          </Link>
        </div>

        <div className="mt-6 text-center">
          <Link 
            to="/contact" 
            className="text-sm font-medium text-primary-600 hover:text-primary-500"
          >
            Need help? Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
}
